import React, { useState, useEffect } from 'react';
import { Globe, AlertCircle, ExternalLink, Search, Activity } from 'lucide-react';
import { moduleService } from '../../../services/modules';
import { DomainAsset, DomainHealthStatus } from '../../../modules/domain-manager/api';

interface PortfolioViewProps {
    onExecutionStarted: (id: string) => void;
    setActiveTab: (id: string) => void;
    onQuickSearch: (query: string) => void;
}

export const PortfolioView = ({ onExecutionStarted, setActiveTab, onQuickSearch }: PortfolioViewProps) => {
    const [domains, setDomains] = useState<DomainAsset[]>([]);
    const [healths, setHealths] = useState<Record<string, DomainHealthStatus>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const loadPortfolio = async () => {
        try {
            setLoading(true);
            const { result } = await moduleService.executeModule('domain_manager', {
                action: 'listDomains',
            });
            setDomains(result);

            // Fetch health for all domains in parallel to avoid sequential waterfalls
            const healthResults = await Promise.all(
                result.map(async (d: DomainAsset) => {
                    try {
                        const { result: health } = await moduleService.executeModule('domain_manager', {
                            action: 'getDomainHealth',
                            domain: d.domain,
                        });
                        return { domain: d.domain, health };
                    } catch {
                        return { domain: d.domain, health: null };
                    }
                })
            );

            const healthMap: Record<string, DomainHealthStatus> = {};
            healthResults.forEach(res => {
                if (res.health) {healthMap[res.domain] = res.health;}
            });
            setHealths(healthMap);

        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadPortfolio(); }, []);

    const isExpiringSoon = (dateString: string) => {
        const expiry = new Date(dateString);
        const now = new Date();
        const diff = expiry.getTime() - now.getTime();
        const days = diff / (1000 * 60 * 60 * 24);
        return days > 0 && days < 30;
    };

    const expiringDomains = domains.filter(d => isExpiringSoon(d.expiryDate));

    const handleQuickSearch = (e: React.FormEvent) => {
        e.preventDefault();
        onQuickSearch(searchQuery);
    };

    if (loading) {return <div className="flex items-center justify-center h-full text-slate-400 font-medium animate-pulse">Loading Portfolio...</div>;}
    if (error) {return <div className="p-6 text-center text-rose-500 flex flex-col items-center gap-2">
        <AlertCircle size={32} />
        <p className="text-sm font-bold">{error}</p>
        <button onClick={loadPortfolio} className="mt-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest">Retry</button>
    </div>;}

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Domain Portfolio</h2>
                    <p className="text-xs text-slate-400 font-medium">Manage your global digital real estate</p>
                </div>
                <div className="flex gap-2">
                    <span className="px-3 py-1 bg-slate-100 text-slate-500 text-[10px] font-black rounded-full uppercase tracking-widest">
                        {domains.length} Assets
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                    <form onSubmit={handleQuickSearch} className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Quick search for a new domain..."
                            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm font-medium focus:ring-2 ring-blue-500 outline-none transition-all shadow-sm"
                        />
                        <button
                            type="submit"
                            className="absolute right-2 top-2 bottom-2 px-4 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95"
                        >
                            Check
                        </button>
                    </form>
                </div>
            </div>

            {expiringDomains.length > 0 && (
                <div className="p-4 bg-rose-50 border border-rose-100 rounded-3xl space-y-3 animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center gap-2 text-rose-600">
                        <AlertCircle size={16} />
                        <span className="text-xs font-black uppercase tracking-widest">Critical Expiry Alerts</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {expiringDomains.map(d => (
                            <div key={d.id} className="flex items-center justify-between p-2 bg-white/60 rounded-xl border border-rose-100">
                                <span className="text-xs font-bold text-slate-700">{d.domain}</span>
                                <span className="text-[10px] font-black text-rose-500 uppercase">{d.expiryDate}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 gap-4">
                {domains.length === 0 ? (
                    <div className="p-12 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200 text-slate-400 font-medium text-sm">
                        No domains found in your portfolio.
                    </div>
                ) : (
                    domains.map(domain => {
                        const health = healths[domain.domain];
                        const healthColor = {
                            healthy: 'bg-emerald-100 text-emerald-600',
                            warning: 'bg-amber-100 text-amber-600',
                            critical: 'bg-rose-100 text-rose-600',
                        }[health?.overallHealth || 'healthy'];

                        return (
                            <div key={domain.id} className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm flex items-center justify-between hover:border-blue-500 transition-all">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
                                        <Globe size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-sm font-bold text-slate-800">{domain.domain}</h3>
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${healthColor}`}>
                                                {health?.overallHealth || 'checking...'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] font-medium text-slate-400">{domain.registrar}</span>
                                            <span className="text-slate-200">•</span>
                                            <span className="text-[10px] font-medium text-slate-400">NS: {domain.nameservers}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="text-right">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Expires</p>
                                        <p className={`text-xs font-bold ${domain.status === 'expired' ? 'text-rose-500' : 'text-slate-700'}`}>
                                            {domain.expiryDate}
                                        </p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                        domain.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
                                    }`}>
                                        {domain.status}
                                    </span>
                                    <button className="p-2 text-slate-300 hover:text-slate-800 transition-colors">
                                        <ExternalLink size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
